// Persistence for the Matterport lead gate.
//
// Mirrors the meta-model engine's scheme 1:1 so the two surfaces behave
// identically (and a future MM <-> MP "ask once" orchestration can reason
// about the same keys):
//   leadgate:deviceId                 -> stable per-browser id (uuid)
//   leadgate:unlock:v1:<projectKey>   -> { expiresAt, deviceId, customerId }
//
// An unlock record suppresses the gate for UNLOCK_TTL_MS (30 days), per
// project — same as MM. localStorage is per-origin, so this lives on whatever
// origin renders the gate; cross-origin (MM iframe vs our share page) is a
// separate orchestration concern handled elsewhere.

const DEVICE_ID_KEY = "leadgate:deviceId";
const UNLOCK_PREFIX = "leadgate:unlock:v1:";

/** 30 days — must match the engine's lead-gate TTL. */
export const UNLOCK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const safeLocal = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    // private mode / blocked third-party storage (e.g. Safari ITP)
    return null;
  }
};

const makeUuid = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to manual */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** Stable per-browser device id — generated once, reused thereafter. */
export const getDeviceId = (): string => {
  const ls = safeLocal();
  if (!ls) return makeUuid(); // ephemeral when storage is blocked
  let id = "";
  try {
    id = String(ls.getItem(DEVICE_ID_KEY) || "").trim();
  } catch {
    /* ignore */
  }
  if (id) return id;
  id = makeUuid();
  try {
    ls.setItem(DEVICE_ID_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
};

const unlockKey = (projectKey: string) => `${UNLOCK_PREFIX}${projectKey}`;

export interface UnlockRecord {
  expiresAt: number;
  deviceId: string;
  customerId: string;
}

/** Live unlock record for this project, or null if absent / expired. */
export const getActiveUnlock = (projectKey: string): UnlockRecord | null => {
  const ls = safeLocal();
  if (!ls || !projectKey) return null;
  let raw = "";
  try {
    raw = String(ls.getItem(unlockKey(projectKey)) || "");
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw) as UnlockRecord;
    if (!rec || typeof rec.expiresAt !== "number") return null;
    if (rec.expiresAt <= Date.now()) {
      try {
        ls.removeItem(unlockKey(projectKey));
      } catch {
        /* ignore */
      }
      return null;
    }
    return rec;
  } catch {
    return null;
  }
};

/** Write a 30-day unlock for this project after a successful capture. */
export const writeUnlock = (projectKey: string, customerId: string): UnlockRecord => {
  const rec: UnlockRecord = {
    expiresAt: Date.now() + UNLOCK_TTL_MS,
    deviceId: getDeviceId(),
    customerId: String(customerId || ""),
  };
  const ls = safeLocal();
  if (ls && projectKey) {
    try {
      ls.setItem(unlockKey(projectKey), JSON.stringify(rec));
    } catch {
      /* ignore */
    }
  }
  return rec;
};
